import { profile } from "console";
import React from "react";

const impactData = [
  {
    label: "Volunteer Hours This Week",
    value: "312",
    icon: "⏱️",
    color: "from-green-400 to-green-600",
  },
  {
    label: "Events Booked Today",
    value: "5",
    icon: "📅",
    color: "from-blue-400 to-blue-600",
  },
  {
    label: "Top Performing NGO",
    value: "SkillUp Mumbai",
    icon: "🏆",
    color: "from-yellow-400 to-orange-500",
  },
];

const quickActions = [
  { label: "Create Event", icon: "➕" },
  { label: "Add NGO", icon: "🧩" },
  { label: "Auto-send Report", icon: "📤" },
];

const savedResponses = [
  "“Thank you for your interest in volunteering. Our team will get in touch soon.”",
  "“Please submit your post-event report by Friday for impact tracking.”",
  "“Here’s a summary of your CSR activities this quarter.”",
];

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Phone, ChevronDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/components/ui/use-toast";

interface PhoneNumber {
  id: number;
  name: string;
  phone: string;
  user_id: string;
  status: boolean;
  created_at: string;
  user: {
    id: string;
    full_name: string;
  };
}

const dailyPrompts = [
  {
    icon: "👀",
    message:
      "You haven’t booked any STEM volunteering this month. Want me to suggest 3 options?",
    action: "Suggest STEM Volunteering",
  },
  {
    icon: "📈",
    message:
      "Corporate engagement is 24% higher on Tuesdays—want to schedule your next mailer?",
    action: "Schedule Mailer",
  },
  {
    icon: "🎯",
    message: "Set a new impact goal for your team this week!",
    action: "Set Impact Goal",
  },
];

const pinnedConversations = [
  "Impact summary for Q1",
  "List of NGOs aligned to SDG 4 in Karnataka",
  "Volunteer engagement stats for March",
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 5) return "🌙 Good night";
  if (hour < 12) return "☀️ Good morning";
  if (hour < 17) return "🌤️ Good afternoon";
  if (hour < 21) return "🌆 Good evening";
  return "🌙 Good night";
};

const impactFacts = [
  "🌱 Did you know? Every hour volunteered can impact 10 lives.",
  "📚 Fact: NGOs aligned to SDG 4 can boost literacy rates by 15% in their region.",
  "🤝 Companies with active CSR see 30% higher employee retention.",
  "🌍 Small actions, big impact—your efforts matter!",
  "💡 1 in 3 employees want more volunteering opportunities at work.",
];

export const SmartSidebar: React.FC<{
  session: any | null;
  profile: any | null;
}> = ({ session, profile }) => {
  const [promptIdx, setPromptIdx] = useState(0);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [factIdx, setFactIdx] = useState(0);
  const [isLoadingNumbers, setIsLoadingNumbers] = useState(false);
  const [isCalling, setIsCalling] = useState<number | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { toast } = useToast();

  const handleCallClick = async (e: React.MouseEvent, id: number) => {
    e.preventDefault();

    try {
      setIsCalling(id);
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/coraa/call/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to initiate call");
      }

      const result = await response.json();
      console.log("Call initiated:", result);

      // Show success toast and close popover
      toast({
        title: "Call initiated",
        description: "The recipient will be notified shortly.",
        variant: "default",
      });

      // Close the popover after a short delay
      setTimeout(() => {
        setIsPopoverOpen(false);
      }, 500);
    } catch (error) {
      console.error("Error initiating call:", error);
      toast({
        title: "Error",
        description: "Failed to initiate call. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsCalling(null);
    }
  };

  const userName = profile?.first_name || profile?.org_name || "there";

  useEffect(() => {
    const interval = setInterval(() => {
      setPromptIdx((prev) => (prev + 1) % dailyPrompts.length);
      setFactIdx((prev) => (prev + 1) % impactFacts.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  // Pinned click handler
  const handlePinnedClick = (pin: string, idx: number) => {
    navigator.clipboard.writeText(pin);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 1300);
  };

  return (
    <aside className="w-full max-w-xs min-w-[260px] flex-shrink-0 px-2 pt-2 pb-6 sticky top-4 hidden lg:block">
      {/* Coraa Avatar with Gradient Glow */}
      <div className="flex flex-col items-center mb-4">
        <div className="relative flex items-center justify-center">
          {/* Gradient Glow Pulse */}
          <span
            className={`absolute w-20 h-20 rounded-full bg-gradient-to-tr from-purple-300 via-fuchsia-300 to-purple-500 opacity-70 -z-10 ${
              factIdx === 0 ? "animate-glow-pulse" : ""
            }`}
            aria-hidden="true"
            style={{ filter: "blur(16px)" }}
          ></span>
          <div className="rounded-full bg-white shadow-lg p-1 animate-bounce z-10 overflow-hidden h-14 w-14 flex items-center justify-center">
            <img
              src="/images/coraa-mascot.png"
              alt="Coraa Mascot"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <div className="text-xs text-purple-700 font-semibold tracking-wide">
            Coraa
          </div>
          <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 rounded-full bg-green-100 hover:bg-green-200 text-green-700 relative"
                onClick={async () => {
                  if (phoneNumbers.length === 0) {
                    try {
                      setIsLoadingNumbers(true);
                      const response = await fetch(
                        `${
                          import.meta.env.VITE_CC_API_URL
                        }/v1/coraa/phone-numbers`,
                        {
                          method: "GET",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${session?.access_token}`,
                          },
                        }
                      );
                      if (!response.ok)
                        throw new Error("Failed to fetch phone numbers");
                      const { data } = await response.json();
                      setPhoneNumbers(data || []);
                    } catch (error) {
                      console.error("Error fetching phone numbers:", error);
                      alert(
                        "Failed to load contact numbers. Please try again."
                      );
                    } finally {
                      setIsLoadingNumbers(false);
                    }
                  }
                }}
                disabled={isLoadingNumbers}
              >
                {isLoadingNumbers ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Phone className="h-3 w-3" />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="end">
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-center text-gray-700 mb-1">
                  Request Callback to
                </h4>
                {phoneNumbers.length > 0 ? (
                  phoneNumbers.map((number) => (
                    <button
                      key={number.id}
                      onClick={(e) => handleCallClick(e, number.id)}
                      disabled={isCalling === number.id}
                      className="w-full text-left flex flex-col p-2 text-sm rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{number.name}</span>
                        {isCalling === number.id && (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {number.phone}
                      </span>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center p-2">
                    {isLoadingNumbers
                      ? "Loading..."
                      : "No contact numbers available"}
                  </p>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <style>{`
          @keyframes glowPulse {
            0%, 100% { opacity: 0.7; transform: scale(1); }
            40% { opacity: 1; transform: scale(1.12); }
            70% { opacity: 0.7; transform: scale(1); }
          }
          .animate-glow-pulse {
            animation: glowPulse 1.3s cubic-bezier(0.4,0,0.2,1) 1;
          }
        `}</style>
      </div>

      {/* Contextual Welcome & Mood */}
      <div className="mb-5">
        <div className="rounded-2xl bg-gradient-to-tr from-green-100 to-green-200 text-green-900 shadow p-4 flex flex-col gap-1 animate-fadeIn">
          <div className="text-lg font-bold leading-tight">
            {getGreeting()}, {userName}!{" "}
            <span className="text-base font-normal">
              Ready to make an impact today?
            </span>
          </div>
          <div className="text-xs font-medium text-green-800 opacity-90 pt-1 animate-fadeIn">
            {impactFacts[factIdx]}
          </div>
        </div>
      </div>

      {/* Impact Cards */}
      <div className="space-y-4 mb-8">
        {impactData.map((item, idx) => (
          <div
            key={idx}
            className={`rounded-2xl bg-gradient-to-tr ${item.color} text-white shadow-xl p-4 flex items-center gap-3 animate-fadeIn`}
          >
            <span className="text-2xl md:text-3xl drop-shadow-lg animate-pulse">
              {item.icon}
            </span>
            <div>
              <div className="text-lg font-bold leading-tight">
                {item.value}
              </div>
              <div className="text-xs opacity-90">{item.label}</div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: none; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.7s cubic-bezier(0.4,0,0.2,1) both;
        }
      `}</style>
    </aside>
  );
};

export default SmartSidebar;
